import { SvgIcon } from "@/svg";
import { AllBusiness, isAllBusiness } from "@/api";
import { Business } from "@norma-bi/bi-api";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { Loader } from "../Loader";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useIsHovered } from "@/utils/useIsHovered";
import { customToast } from "@/utils/customToast";
import { BusinessAvatar } from "./BusinessAvatar";
import clsx from "clsx";
import { useGetBusinessStatus } from "@/hooks/useGetBusinessStatus";

export type BusinessPickerProps = {
  businesses: ReturnType<typeof useGetBusinesses>;
  onClose: () => void;
  onBusinessChange?: (id: string) => Promise<void>;
};

export function BusinessPicker(props: BusinessPickerProps) {
  const businessContext = useBusinessContext();

  async function onSelectBusiness(business: Business) {
    try {
      await (props.onBusinessChange ?? businessContext.setBusiness)(business.id);
      customToast("Business changed successfully", "success", "bottom");
      props.onClose();
    } catch (e) {
      console.error("could not change business context:", e);
    }
  }

  const AllBusinessesEntry = (
    <BusinessEntry
      business={AllBusiness}
      isSelected={Boolean(businessContext.business) && isAllBusiness(businessContext.business!.id)}
      onSelect={() => onSelectBusiness(AllBusiness)}
      iconClassName={"bg-cyan"}
    />
  );

  return (
    <div className="flex flex-col">
      {!props.businesses.isSuccess ? (
        <Loader />
      ) : (
        <>
          {props.businesses.data.length > 1 && AllBusinessesEntry}
          {props.businesses.data.map((b) => {
            const isSelected = businessContext.business?.id === b.id;
            return (
              <BusinessEntry
                key={b.id}
                business={b}
                onSelect={() => onSelectBusiness(b)}
                isSelected={isSelected}
                iconClassName={"bg-primaryColor"}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

type BusinessEntryProps = {
  business: Business;
  onSelect: () => void;
  isSelected: boolean;
  iconClassName?: string;
};

function BusinessEntry(props: BusinessEntryProps) {
  const { props: buttonProps, isHovered } = useIsHovered<HTMLButtonElement>();

  const businessStatus = useGetBusinessStatus(props.business);

  const isSelectable = businessStatus === "available";

  function onClick() {
    if (props.isSelected) {
      return;
    }
    if (businessStatus === "unlicensed") {
      customToast(
        isAllBusiness(props.business.id)
          ? "Some of your Businesses don't have the necessary Addon/License to continue using Foodics BI. Please contact your account manager to extend your free trial or enable the addon."
          : "This Business doesn't have the necessary Addon/License to continue using Foodics BI. Please contact your account manager to extend your free trial or enable the addon.",
        "error",
        "bottom",
        {
          duration: 5000,
        },
      );
      return;
    }

    if (businessStatus === "onboarding") {
      customToast(
        isAllBusiness(props.business.id)
          ? "We haven't retrieved the data of some of your Businesses, yet. This may take up to 30 minutes."
          : "We haven't retrieved the data of this Business, yet. This may take up to 30 minutes.",
        "success",
        "bottom",
      );
      return;
    }
    if (businessStatus === "installation_failed" || businessStatus === "status_error") {
      customToast(
        businessStatus === "installation_failed"
          ? "Installation could not be completed. Please contact support."
          : "Could not check installation status. Please try again shortly.",
        "error",
        "bottom",
      );
      return;
    }
    props.onSelect();
  }

  let unavailabilityIndication: React.ReactNode = null;
  if (businessStatus !== "available") {
    let message = "Unavailable";
    if (businessStatus === "unlicensed") {
      message = "Unlicensed";
    } else if (businessStatus === "onboarding") {
      message = "Pending";
    } else if (businessStatus === "installation_failed") {
      message = "Installation failed";
    } else if (businessStatus === "status_error") {
      message = "Status unavailable";
    }
    unavailabilityIndication = (
      <span className="text-xs text-feintGray font-normal">{message}</span>
    );
  }

  return (
    <button
      {...buttonProps}
      className={clsx(
        "flex items-center p-4 gap-2 hover:bg-backgroundGray select-none w-full",
        (!isSelectable || props.isSelected) && "cursor-not-allowed",
        !isSelectable && "opacity-60",
      )}
      onClick={onClick}
    >
      <div className="text-pureBlack">
        <SvgIcon icon="More" />
      </div>
      <BusinessAvatar
        businessDisplayName={props.business.display_name}
        className={props.iconClassName}
      />
      <div className="flex justify-between items-center flex-1">
        <div className="flex flex-col text-sm text-left">
          <span className="content-label">{props.business.display_name}</span>
          {unavailabilityIndication}
        </div>
        <div className="flex flex-row items-center">
          {props.isSelected && (
            <div className="text-sematicGreen">
              <SvgIcon icon="Check" />
            </div>
          )}
          {isHovered && isSelectable && !props.isSelected && (
            <div className={"text-dark"}>
              <SvgIcon icon={"Check"} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
